import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { SiteLayout, CtaBand } from "../components/site";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { RevealLines, Reveal, ContactLinks, LogoMark } from "../components/shared";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { EnquiryBtn } from "../components/enquiry";
import { TEAM, TEAM_GROUPS } from "../team";
import { CONTACT, ACKNOWLEDGEMENT, TAGLINE } from "../content";

const Caps = ({ children, className = "" }) => <p className={`font-jakarta text-xs uppercase tracking-[0.32em] text-[#5E6B57] ${className}`}>{children}</p>;
const photo = (id) => `/images/team/${id}.jpg`;

const Nav = () => (
  <header className="absolute top-0 inset-x-0 z-40">
    <div className="max-w-[1360px] mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
      <Link to="/" data-testid="team-nav-logo" className="flex items-center gap-3">
        <LogoMark className="w-12 h-12" />
        <span className="leading-none">
          <span className="block font-newsreader text-xl tracking-[0.12em] text-[#3F4A3C]">INNER YOU</span>
          <span className="block font-jakarta text-[10px] tracking-[0.38em] text-[#5E6B57] mt-1">DEVELOPMENT</span>
        </span>
      </Link>
      <Link to="/" data-testid="team-nav-home" className="hidden sm:inline-flex items-center gap-2 font-jakarta text-sm text-[#4A4F45] hover:text-[#3F4A3C]"><ArrowLeft className="w-4 h-4" /> Home</Link>
      <a href={CONTACT.phoneHref} data-testid="team-nav-call" className="font-jakarta text-sm rounded-full bg-[#3F4A3C] px-5 py-2.5 text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors duration-500">Give us a call</a>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative pt-16 sm:pt-24 pb-16 sm:pb-24 overflow-hidden" data-testid="team-hero">
    <img src="/images/leaf-merch-corner.png" alt="" aria-hidden className="pointer-events-none select-none absolute top-4 right-3 sm:right-8 hidden sm:block w-52 lg:w-72 opacity-95" />
    <div className="relative max-w-[1360px] mx-auto px-6 sm:px-10">
      <Caps>{TAGLINE}</Caps>
      <RevealLines lines={["Meet the people", "who walk with you."]} emIndex={1} emClass="italic text-[#5F7C8A]" className="mt-8 font-newsreader text-[#3F4A3C] text-5xl sm:text-7xl lg:text-[5.6rem] leading-[1] tracking-tight" />
      <Reveal delay={0.6} className="mt-10 max-w-xl">
        <span className="block w-14 h-px bg-[#D9A85A] mb-8" />
        <p className="font-jakarta text-base sm:text-lg leading-relaxed text-[#4A4F45]">Psychologists, social workers, counsellors and allied health practitioners, working as one team.</p>
      </Reveal>
    </div>
  </section>
);

const BOOK_TYPE = {
  steven: "social-work", tasya: "psychology-adults", karan: "psychology-adults", ninwe: "psychology-all-ages",
  jodie: "social-work", tazneem: "social-work", nicole: "counselling", kate: "mental-health-ot",
  taruna: "occupational-therapy", garima: "speech-therapy", malika: "dietitian", holly: "parenting",
};

const Capacity = ({ m, className = "" }) => m.capacity ? (
  <div data-testid={`capacity-${m.id}`} className={`rounded-[20px] bg-[#F3DCA8]/60 px-5 py-4 font-jakarta text-xs sm:text-sm leading-relaxed text-[#3F4A3C] text-left space-y-2 ${className}`}>
    {m.capacity.map((t, i) => <p key={i} className={i === 0 ? "flex gap-2 font-medium" : ""}>{i === 0 && <span className="w-2 h-2 mt-1.5 shrink-0 rounded-full bg-[#C59B45]" />}<span>{t}</span></p>)}
  </div>
) : null;

const Featured = ({ onOpen }) => {
  const s = TEAM[0];
  return (
    <section className="px-4 sm:px-8" data-testid="team-featured">
      <div className="max-w-[1360px] mx-auto rounded-[40px] bg-[#E3E9E4] grid lg:grid-cols-12 gap-10 p-6 sm:p-12 lg:p-16 items-center">
        <Reveal className="lg:col-span-5">
          <div className="aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[28px] bg-[#F6F2E9]">
            <img src={photo(s.id)} alt={s.name} className="w-full h-full object-cover object-top" />
          </div>
          <Capacity m={s} className="mt-4" />
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.15}>
          <Caps>Founder & Director</Caps>
          <h2 className="mt-5 font-newsreader text-4xl sm:text-6xl text-[#3F4A3C]">{s.name}</h2>
          <p className="mt-3 font-jakarta text-sm text-[#5F7C8A]">{s.role}</p>
          <p className="mt-2 font-jakarta text-xs text-[#5E6B57] leading-relaxed">{s.creds}</p>
          <p className="mt-8 font-jakarta text-base sm:text-lg leading-relaxed text-[#4A4F45]">{s.bio[0]}</p>
          <button onClick={() => onOpen(s)} data-testid="team-featured-read-btn" className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[#3F4A3C] px-7 py-4 font-jakarta text-sm text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors duration-500">
            Read Steven's story <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

const Card = ({ m, onOpen }) => (
  <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
    onClick={() => onOpen(m)} data-testid={`team-card-${m.id}`} className="group text-left self-start">
    <div className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[24px] bg-[#E3E9E4]">
      <img src={photo(m.id)} alt={m.name} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
      <span className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-[#F6F2E9]/90 backdrop-blur grid place-items-center text-[#3F4A3C] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500"><ArrowUpRight className="w-4 h-4" /></span>
    </div>
    <h3 className="mt-5 font-newsreader text-2xl text-[#3F4A3C]">{m.name}</h3>
    <p className="mt-1 font-jakarta text-sm text-[#5F7C8A]">{m.role}</p>
    {m.focus && <div className="mt-3 flex flex-wrap gap-1.5">{m.focus.slice(0, 2).map((f) => <span key={f} className="rounded-full bg-[#E3E9E4] px-2.5 py-1 font-jakarta text-[11px] text-[#3F4A3C]">{f}</span>)}</div>}
  </motion.button>
);

const Grid = ({ onOpen }) => {
  const [group, setGroup] = useState("All");
  const list = TEAM.slice(1).filter((m) => group === "All" || m.group === group);
  return (
    <section className="py-24 sm:py-32" data-testid="team-grid-section">
      <div className="max-w-[1360px] mx-auto px-6 sm:px-10">
        <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div><Caps>Our practitioners</Caps><h2 className="mt-5 font-newsreader text-4xl sm:text-5xl text-[#3F4A3C]">Many disciplines, <em className="text-[#5F7C8A]">one team.</em></h2></div>
          <div className="flex flex-wrap gap-2" data-testid="team-filters">
            {TEAM_GROUPS.map((g) => (
              <button key={g} onClick={() => setGroup(g)} data-testid={`team-filter-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-full px-4 py-2 font-jakarta text-xs sm:text-sm transition-colors duration-300 ${group === g ? "bg-[#3F4A3C] text-[#F6F2E9]" : "bg-[#E3E9E4] text-[#3F4A3C] hover:bg-[#DCE6EC]"}`}>{g}</button>
            ))}
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 items-start gap-x-5 gap-y-12 sm:gap-x-8" data-testid="team-grid">
          <AnimatePresence initial={false}>{list.map((m) => <Card key={m.id} m={m} onOpen={onOpen} />)}</AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const Bio = ({ m, onClose }) => (
  <Dialog open={!!m} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent data-lenis-prevent className="max-w-4xl w-[94vw] max-h-[88vh] overflow-y-auto rounded-[28px] border-0 bg-[#F6F2E9] p-0" data-testid="team-bio-dialog">
      {m && (
        <div className="grid md:grid-cols-5">
          <div className="md:col-span-2 bg-[#E3E9E4] p-6 sm:p-8">
            <div className="aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[20px]"><img src={photo(m.id)} alt={m.name} className="w-full h-full object-cover object-top" /></div>
            <Capacity m={m} className="mt-4" />
            {m.focus && <div className="mt-6 flex flex-wrap gap-2">{m.focus.map((f) => <span key={f} className="rounded-full bg-[#F6F2E9] px-3 py-1.5 font-jakarta text-xs text-[#3F4A3C]">{f}</span>)}</div>}
          </div>
          <div className="md:col-span-3 p-6 sm:p-10">
            <DialogTitle className="font-newsreader font-normal text-3xl sm:text-4xl text-[#3F4A3C]">{m.name}</DialogTitle>
            <DialogDescription className="mt-2 font-jakarta text-sm text-[#5F7C8A]">{m.role}</DialogDescription>
            {m.creds && <p className="mt-2 font-jakarta text-xs text-[#5E6B57]">{m.creds}</p>}
            <span className="block w-12 h-px bg-[#D9A85A] my-6" />
            {m.bio.map((p) => <p key={p.slice(0, 20)} className="mb-4 font-jakarta text-[15px] leading-relaxed text-[#4A4F45]">{p}</p>)}
            <div className="mt-4 flex flex-wrap gap-3">
              {BOOK_TYPE[m.id] && <Link to={`/book?type=${BOOK_TYPE[m.id]}`} data-testid="team-bio-book-btn" className="inline-flex items-center gap-2 rounded-full bg-[#3F4A3C] px-6 py-3 font-jakarta text-sm text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors">Book with {m.name.split(" ")[0]} <ArrowUpRight className="w-4 h-4" /></Link>}
              <EnquiryBtn testid="team-bio-enquiry-btn" className="!py-3" />
            </div>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const Footer = () => (
  <footer className="bg-[#3F4A3C] text-[#F6F2E9] pt-20 pb-28" data-testid="footer">
    <div className="max-w-[1360px] mx-auto px-6 sm:px-10">
      <p className="font-script text-4xl sm:text-5xl text-[#F3DCA8]">Let's work together</p>
      <h2 className="mt-4 font-newsreader text-4xl sm:text-5xl max-w-2xl">Not sure who to see? We'll check which practitioner suits you, so you don't have to.</h2>
      <ContactLinks prefix="team-footer" className="mt-10 flex flex-col sm:flex-row gap-6 sm:gap-12 font-jakarta text-lg" linkClass="hover:text-[#F3DCA8] transition-colors" />
      <p className="mt-16 pt-8 border-t border-white/15 font-newsreader italic text-lg leading-relaxed text-[#EEEADF]">{ACKNOWLEDGEMENT}</p>
    </div>
  </footer>
);

export default function Team() {
  const [open, setOpen] = useState(null);
  const { hash } = useLocation();
  useEffect(() => {
    const m = TEAM.find((t) => `#${t.id}` === hash);
    if (m) setOpen(m);
  }, [hash]);
  return (
    <SiteLayout testid="team-page">
      <Hero />
      <Featured onOpen={setOpen} />
      <Grid onOpen={setOpen} />
      <CtaBand />
      <Bio m={open} onClose={() => setOpen(null)} />
    </SiteLayout>
  );
}
